import { useEffect, useState } from 'react';
import { OneFlowError } from '../../core/errors';
import { formatPercent } from '../../core/format';
import { engine } from '../../state/store';
import { createObjectUrl, revokeObjectUrl } from '../../engine/object-urls';
import { useSession } from '../hooks/useSession';
import { StatusStage } from './StatusStage';

type Fit = 'fit' | 'actual';

/**
 * Images are shown from a file-backed Blob URL of the complete local copy, so a
 * large image is never copied into memory. SVG is shown through <img>, where
 * scripts cannot run. ALG-044, ALG-045
 */
export function ImageStage({ id }: { id: string }) {
  const { record, complete, fraction } = useSession(id);
  const [url, setUrl] = useState<string | null>(null);
  const [dims, setDims] = useState<{ w: number; h: number } | null>(null);
  const [fit, setFit] = useState<Fit>('fit');
  const [zoom, setZoom] = useState(1);
  const [failed, setFailed] = useState(false);
  const container = record?.container;

  useEffect(() => {
    if (!complete) return;
    let alive = true;
    let made: string | null = null;
    setFailed(false);
    engine
      .getCompleteFile(id)
      .then((file) => {
        if (!alive) return;
        const blob = container === 'svg' ? new Blob([file], { type: 'image/svg+xml' }) : file;
        made = createObjectUrl(`image:${id}`, blob);
        setUrl(made);
      })
      .catch((e) => {
        if (alive) setFailed(e instanceof OneFlowError || true);
      });
    return () => {
      alive = false;
      revokeObjectUrl(made);
      setUrl(null);
      setDims(null);
    };
  }, [id, complete, container]);

  if (container === 'heic') {
    return (
      <StatusStage
        kind="image"
        title="This browser cannot show HEIC images"
        body={
          complete
            ? 'Export the original to open it in another app.'
            : 'The download continues. Export the original when it is complete.'
        }
      />
    );
  }
  if (!complete) {
    return (
      <StatusStage
        kind="image"
        title="The image appears once it is fully saved"
        body={`Saved so far: ${formatPercent(fraction)}`}
        busy
      />
    );
  }
  if (failed)
    return (
      <StatusStage
        kind="image"
        title="The image could not be shown"
        body="The saved file is intact. Export the original to open it elsewhere."
      />
    );

  return (
    <div className="viewer image-viewer">
      <div className="viewer__bar">
        <div className="seg" role="group" aria-label="Image size">
          <button
            type="button"
            className="seg__btn"
            aria-pressed={fit === 'fit'}
            onClick={() => {
              setFit('fit');
              setZoom(1);
            }}
          >
            Fit
          </button>
          <button
            type="button"
            className="seg__btn"
            aria-pressed={fit === 'actual'}
            onClick={() => {
              setFit('actual');
              setZoom(1);
            }}
          >
            100%
          </button>
        </div>
        <button
          type="button"
          className="btn btn--quiet"
          onClick={() => setZoom((z) => Math.max(0.25, z / 1.25))}
          aria-label="Zoom out"
        >
          −
        </button>
        <span className="mono viewer__readout">{Math.round(zoom * 100)}%</span>
        <button
          type="button"
          className="btn btn--quiet"
          onClick={() => setZoom((z) => Math.min(6, z * 1.25))}
          aria-label="Zoom in"
        >
          +
        </button>
        {dims && (
          <span className="viewer__readout mono">
            {dims.w} × {dims.h}
          </span>
        )}
      </div>
      <div className="image-viewer__scroll" tabIndex={0} role="region" aria-label="Image preview">
        {url && (
          <img
            className={`image-viewer__img image-viewer__img--${fit}`}
            style={{ transform: `scale(${zoom})` }}
            src={url}
            alt={record?.name ?? 'Image'}
            onLoad={(e) =>
              setDims({ w: e.currentTarget.naturalWidth, h: e.currentTarget.naturalHeight })
            }
            onError={() => setFailed(true)}
            draggable={false}
          />
        )}
      </div>
    </div>
  );
}
